import { FC, useCallback, useState, useEffect, useMemo } from 'react';
import { usePGettext, useTranslations } from '@cranium/i18n';
import { Select, Typography, Collapse } from 'antd';
import Flags from '@/assets/images/flags';
import styled from 'styled-components';
import get from 'lodash/get';
import { client } from '@/shared/i18n/TranslationsProvider';
import { useGetClientSettings, usePartnerData } from '@/shared/access';
import { isEmpty } from '@/libs';

const Flag = styled.img`
  width: 28px;
  border-radius: 2px;
`;

const FlagSmall = styled.img`
  width: 22px;
  margin-right: 8px;
  border-radius: 2px;
`;

const InApp = styled.span`
  width: 22px;
  margin-right: 8px;
  height: 22.5px;
  text-align: center;
  display: inline-block;
  &:before {
    display: inline;
    line-height: 22.5px;
  }
`;

const DropDown = styled(Select)`
  .ant-select-selector {
    padding: 0 !important;
    height: auto !important;
    line-height: 0 !important;
    &:after {
      display: none !important;
    }
    .ant-select-selection-search {
      display: none !important;
    }
    .ant-select-selection-item {
      line-height: 0 !important;
    }
  }
  img,
  .icon-edit {
    margin-right: 0;
  }
`;

const Container = styled.div`
  display: flex;
  align-items: center;
  padding: 0 8px;
  border-left: 1px solid var(--gray-1);
  border-right: 1px solid var(--gray-1);
`;

export const ChangeLanguage: FC<{ mobile?: boolean }> = ({ mobile, ...props }) => {
  const { language, setLanguage } = useTranslations();
  const languageSettings = useGetClientSettings('languages');
  const languageValues = isEmpty(languageSettings.values) ? new Set(['en']) : new Set(languageSettings.values);
  const [langs, setLang] = useState<Set<string>>(languageValues);
  useEffect(() => {
    client.getLanguageObjects().then(data => {
      const set = new Set(
        data.map(lang => get(lang, 'id', '').split('-')[0].toLowerCase()).filter(lang => languageValues.has(lang))
      );
      if (!set.has(language)) {
        setLanguage('en');
      }
      setLang(set);
    });
  }, []);
  const partnerData = usePartnerData();
  const { pgettext } = usePGettext();

  const handleChange = useCallback(
    value => {
      setLanguage(value);
      setTimeout(() => {
        window.location.reload();
      }, 50);
    },
    [setLanguage, language]
  );

  const enFlag = (country: string | undefined) => {
    if (country === 'United States') return Flags.US;
    if (country === 'Australia') return Flags.AU;
    return Flags.CA;
  };

  const list = useMemo(() => {
    return [
      {
        value: 'en',
        flag: {
          src: enFlag(partnerData?.address?.country),
          alt: 'English'
        },
        text: 'English'
      },
      {
        value: 'uk',
        flag: {
          src: Flags.UA,
          alt: 'Ukrainian'
        },
        text: 'Українська'
      },
      {
        value: 'fr',
        flag: {
          src: Flags.FR,
          alt: 'French'
        },
        text: 'Français'
      },
      {
        value: 'it',
        flag: {
          src: Flags.IT,
          alt: 'Italian'
        },
        text: 'Italiano'
      },
      {
        value: 'es',
        flag: {
          src: Flags.ES,
          alt: 'Spanish'
        },
        text: 'Español'
      },
      {
        value: 'ach',
        icon: <InApp className="icon-edit" />,
        text: pgettext('In app translate', 'lang_in_app')
      }
    ];
  }, [langs, partnerData]);

  const options = useMemo(() => {
    return list
      .filter(option => option && langs.has(option.value))
      .map(option =>
        typeof option === 'object' ? (
          <Select.Option key={option.value} value={option.value} label={option.flag ? <Flag {...option.flag} /> : option.icon}>
            {option.flag ? <FlagSmall {...option.flag} /> : option.icon}
            <Typography.Text className="regular-14 gray-9">{option.text}</Typography.Text>
          </Select.Option>
        ) : null
      );
  }, [langs, partnerData, list]);

  const mobileItems = useMemo(() => {
    return list
      .filter(option => option && langs.has(option.value) && option.value !== language)
      .map(option =>
        typeof option === 'object' ? (
          <MobileRow key={option.text} onClick={() => handleChange(option.value)}>
            {option.flag ? <FlagSmall {...option.flag} /> : option.icon}
            <Typography.Text className="regular-14 gray-9">{option.text}</Typography.Text>
          </MobileRow>
        ) : null
      );
  }, [langs, partnerData, list, language]);

  const mobileHeader = useMemo(() => {
    const option = list.find(option => option.value === language) || list[0];
    if (!option) return null;
    return (
      <MobileRow key={option.text}>
        {option.flag ? <FlagSmall {...option.flag} /> : option.icon}
        <Typography.Text className="regular-14 gray-9">{option.text}</Typography.Text>
      </MobileRow>
    );
  }, [list, language]);

  if (!languageSettings.visible) return null;

  if (mobile) {
    return (
      <Collapse.Panel
        header={mobileHeader}
        key="language"
        {...props}
        collapsible={mobileItems.length <= 1 ? 'disabled' : undefined}
      >
        {mobileItems}
      </Collapse.Panel>
    );
  }
  return (
    <Container>
      <DropDown
        value={language}
        defaultValue={language}
        onChange={handleChange}
        optionLabelProp="label"
        showArrow={false}
        dropdownMatchSelectWidth={false}
      >
        {options}
      </DropDown>
    </Container>
  );
};

const MobileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 48px;
  padding-left: 8px;
  cursor: pointer;
  &:hover {
    background: var(--green-2) !important;
    color: var(--gray-9) !important;
    border-radius: 4px !important;
  }
  img {
    margin-right: 0 !important;
  }
  .icon-edit {
    margin: 0;
    font-size: 24px;
    color: var(--gray-7) !important;
  }
  > span:not(.icon-edit) {
    font: var(--font-normal-18) !important;
    color: var(--gray-7) !important;
  }
`;
